'use client';
import { getExampleNumber } from 'libphonenumber-js';
import examples from 'libphonenumber-js/examples.mobile.json';
import { useTranslations } from 'next-intl';
import { forwardRef, useMemo, useRef, useState } from 'react';
import { formatPhoneNumberIntl, getCountries, getCountryCallingCode } from 'react-phone-number-input';
import { cn } from '../../shadcn';
import { AutoComplete } from '../AutoComplete/AutoComplete';
import { FieldInfo } from '../FieldInfo/FieldInfo';
import { FieldMessage } from '../FieldMessage/FieldMessage';
import { TextInput } from '../TextInput/TextInput';
import type { PhoneNumberInputProps, PhonePrefixOption } from '../types';
import { formatNationalPhoneNumberParts } from './phoneNumberUtils';
import { phonePrefixFilter } from './phonePrefixFilter';

const DEFAULT_COUNTRY = 'GB';
const DEFAULT_PREFIX = `${DEFAULT_COUNTRY}+${getCountryCallingCode(DEFAULT_COUNTRY)}`;

const PhoneNumberInput = forwardRef(function PhoneNumberInput(
  {
    id,
    label = '',
    value = { prefix: DEFAULT_PREFIX, number: '' },
    onChange,
    placeholder = '00 000 00 00',
    useDynamicPlaceholder = true,
    formatNationalInput = true,
    hidePrefix = false,
    helpMessage,
    helpMessageMode = 'inline',
    helpMessageType,
    disabled,
    className,
    prefixTrackingProps,
    inputTrackingProps,
    helperTrackingProps,
    ...props
  }: PhoneNumberInputProps,
  ref: React.Ref<HTMLInputElement>
) {
  const [prefixOptions] = useState<PhonePrefixOption[]>(() =>
    getCountries().map(country => ({
      value: `${country}+${getCountryCallingCode(country)}`,
      label: `${country} (+${getCountryCallingCode(country)})`,
    }))
  );
  const t = useTranslations('form');
  const numberInputRef = useRef<HTMLInputElement | null>(null);

  const setNumberInputRef = (node: HTMLInputElement | null) => {
    numberInputRef.current = node;

    if (typeof ref === 'function') {
      ref(node);
      return;
    }

    if (ref) {
      ref.current = node;
    }
  };

  // Formats a user-entered string into a national-format phone number for the given prefix
  // It builds a temporary E.164 value using the selected country dial code, formats it
  // with react-phone-number-input, then strips the dial code to return only the national part
  const formatNationalFrom = (prefix: string, input: string) => {
    const rawDigits = input.replace(/\D/g, '');

    if (!rawDigits) {
      return '';
    }

    if (!formatNationalInput) {
      return rawDigits;
    }

    const dialCode = prefix.split('+')[1] || '';

    if (hidePrefix) {
      return formatNationalPhoneNumberParts(prefix, rawDigits);
    }

    const e164 = dialCode ? `+${dialCode}${rawDigits}` : rawDigits;
    const intl = formatPhoneNumberIntl(e164) || input;

    return dialCode ? intl.replace(new RegExp(`^\\+${dialCode}\\s*`), '') : intl;
  };

  const currentPlaceholder = useMemo(() => {
    if (!useDynamicPlaceholder) {
      return placeholder;
    }
    try {
      const country = value.prefix.split('+')[0];
      const example = getExampleNumber(country as never, examples as never);
      const national = example?.formatNational();
      const zerosOnly = national ? national.replace(/\d/g, '0').replace(/[()-]/g, '').replace(/\s+/g, ' ').trim() : undefined;
      return zerosOnly || placeholder;
    } catch {
      return placeholder;
    }
  }, [value.prefix, placeholder, useDynamicPlaceholder]);

  return (
    <div className={className}>
      <FieldInfo htmlFor={id} label={label} />
      <div className="flex flex-row gap-2 peer/phone-number-input">
        {!hidePrefix && (
          <div className="w-24">
            <AutoComplete
              id={id}
              label=""
              value={value.prefix}
              onValueChange={(newPrefix: string) => {
                const national = formatNationalFrom(newPrefix, value.number || '');
                onChange({ ...value, prefix: newPrefix, number: national });
              }}
              fixedItems={prefixOptions}
              filter={phonePrefixFilter}
              showGroups={false}
              className="w-full"
              disabled={disabled}
              searchPlaceholder={t('placeholders.diallingCode')}
              displayFormatter={value => {
                const countryPrefix = value.split('+')[1];
                return `+${countryPrefix}`;
              }}
              triggerProps={prefixTrackingProps}
            />
          </div>
        )}
        <div className="flex-1">
          <TextInput
            id={`${id}-number`}
            value={value.number}
            onChange={event => {
              const input = event.target;
              const cursorPos = input.selectionStart ?? input.value.length;
              const national = formatNationalFrom(value.prefix, input.value);
              const caret = formatNationalFrom(value.prefix, input.value.slice(0, cursorPos)).length;

              onChange({ ...value, number: national });

              // Replacing the value moves the caret to the end. Put it back after the formatted text that was before it.
              requestAnimationFrame(() => {
                const element = numberInputRef.current;
                if (!element || document.activeElement !== element) {
                  return;
                }

                element.setSelectionRange(caret, caret);
              });
            }}
            placeholder={currentPlaceholder}
            disabled={disabled}
            type={props.type || 'tel'}
            inputMode="numeric"
            helpMessageType={helpMessageType}
            ref={setNumberInputRef}
            inputTrackingProps={inputTrackingProps}
            data-testid="phone-number-input"
            {...props}
          />
        </div>
      </div>
      <FieldMessage
        id={`${id}-error`}
        className={cn(
          helpMessageMode === 'inline-foreground' && 'peer-focus-within/phone-number-input:**:text-foreground',
          helpMessageMode === 'focusout' && 'peer-focus-within/phone-number-input:hidden'
        )}
        message={helpMessage}
        messageType={helpMessageType}
        {...helperTrackingProps}
        data-testid="phone-number-form-phone-number-error"
      />
    </div>
  );
});

export { PhoneNumberInput };
